import React from 'react';
import formatISO from 'date-fns/formatISO';
import { useSWRConfig } from 'swr';
import deleteOpportunityService from '@/services/opportunities/deleteOpportunity';
import postOpportunity from '@/services/opportunities/postOpportunity';
import putOpportunity from '@/services/opportunities/putOpportunity';
import type Opportunity from '@/types/api/Opportunity';
import type PostOpportunity from '@/types/api/PostOpportunity';
import type PutOpportunity from '@/types/api/PutOpportunity';
import removeItemFromListMutation from '@/utils/swr/removeItemFromListMutation';

const DATA_KEY = 'opportunities';
const ID_KEY = 'opportunityId';

export default function useOpportunityServices() {
  const { mutate } = useSWRConfig();

  const createOpportunity = React.useCallback(
    async (data: PostOpportunity) => {
      await postOpportunity(data);

      /* Update all /opportunities cache items */
      mutate(
        (key) => typeof key === 'string' && key.startsWith('opportunities?'),
        undefined,
        { revalidate: true },
      );
    },
    [mutate],
  );

  const removeOpportunityFromList = React.useCallback(
    (opportunityId: string) => {
      /**
       * Remove from all /opportunities cache items that may have the opportunity
       * in it.
       */
      mutate(
        (key) => typeof key === 'string' && key.startsWith('opportunities?'),
        (currentData) =>
          removeItemFromListMutation<
            typeof DATA_KEY,
            typeof ID_KEY,
            Opportunity
          >({
            dataKey: DATA_KEY,
            idKey: ID_KEY,
            itemId: opportunityId,
            currentData,
          }),
        { revalidate: false },
      );
    },
    [mutate],
  );

  const deleteOpportunity = React.useCallback(
    async (opportunityId: string) => {
      await deleteOpportunityService(opportunityId);

      removeOpportunityFromList(opportunityId);
    },
    [mutate],
  );

  /**
   * This is a workaround due to delays in syncing from dynamo to aurora
   * for the opportunities search endpoint
   */
  function timeout(ms: number) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  const updateOpportunity = React.useCallback(
    async (opportunityId: string, opportunity: PutOpportunity) => {
      const updatedOpportunity = await putOpportunity(
        opportunityId,
        opportunity,
      );

      if (!updatedOpportunity) {
        return;
      }

      /**
       * This is a workaround due to delays in syncing from dynamo to aurora
       * for the opportunities search endpoint
       */
      await timeout(500);

      /**
       * Update the individual cache item for /opportunities/:opportunityId
       */
      mutate(
        `opportunities/${opportunityId}`,
        (currentData = {}) => ({
          ...currentData,
          ...updatedOpportunity.data.opportunity,
        }),
        { revalidate: false },
      );

      /**
       * Update all /opportunities cache items
       */
      mutate(
        (key) => typeof key === 'string' && key.startsWith('opportunities?'),
        undefined,
        { revalidate: true },
      );
    },
    [mutate],
  );

  const archiveOpportunity = React.useCallback(
    async (opportunityId: string) => {
      await updateOpportunity(opportunityId, {
        archivedAt: formatISO(new Date()),
      });
    },
    [updateOpportunity],
  );

  const unarchiveOpportunity = React.useCallback(
    async (opportunityId: string) => {
      await updateOpportunity(opportunityId, {
        archivedAt: undefined,
      });
    },
    [updateOpportunity],
  );

  return {
    createOpportunity,
    archiveOpportunity,
    deleteOpportunity,
    unarchiveOpportunity,
    updateOpportunity,
  };
}
